import { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useItemContext } from '../../contexts/ItemContext';
import { getChartItemColor, getItemColor } from '../../utils/colors';

const DEFAULT_STROKE_COLOR = '#eee';

/* A pie chart that shows individual items by displayCost. */
const ItemsChart = ({ data, metrics }) => {
  const { activeKey, setActiveKey, clearActiveKey } = useItemContext();
  const [itemData, categoryData] = useMemo(() => {
    const activeMetrics = metrics.filter(({ shown }) => shown);
    // each metric defines a data accessor, and on that data an accessor and aggregate
    // so, get one value per metric based on this
    const byCategory = activeMetrics.map(
      ({ accessor, aggregator, displayName, color }) => ({
        items: data[accessor],
        color,
        displayName,
        categoryDisplayCost: aggregator(
          data[accessor].map((item) => item.displayCost),
        ),
      }),
    );
    const displayCost = byCategory.reduce(
      (sum, { categoryDisplayCost }) => sum + categoryDisplayCost,
      0,
    );

    // then, for each item, calculate a value for each metric
    const byItem = [];
    byCategory.forEach(({ items, displayName }) => {
      // for each metric, create an "other" item, to lump together items
      // with < 3% of the share of the pie
      const otherDisplayName = byCategory.length > 1 ? ` - ${displayName}` : '';
      const other = {
        displayCost: 0,
        name: `Other${otherDisplayName}`,
        id: `other:${otherDisplayName}`,
      };
      items.forEach((item) => {
        const visible =
          item.id === activeKey || item.displayCost >= displayCost * 0.03;
        if (visible) {
          byItem.push(item);
        } else {
          other.displayCost += item.displayCost;
        }
      });
      if (other.displayCost > 0) {
        byItem.push({ ...other, color: getItemColor(other, items.length) });
      }
    });
    return [byItem, byCategory];
  }, [activeKey, data, metrics]);

  const showInnerRing = metrics.length > 1;

  return (
    <ResponsiveContainer width="99%" height="99%">
      <PieChart width={200} height={200}>
        {showInnerRing && (
          <Pie
            data={categoryData}
            outerRadius="35%"
            fill="#8884d8"
            dataKey="categoryDisplayCost"
            animationDuration={0}
            isAnimationActive={false}
            stroke={categoryData.length > 1 ? DEFAULT_STROKE_COLOR : ''}
          >
            {categoryData.map(({ color }) => (
              <Cell
                key={color}
                fill={getChartItemColor(color, activeKey, color)}
              />
            ))}
          </Pie>
        )}
        <Pie
          data={itemData}
          outerRadius="78%"
          innerRadius={showInnerRing ? '35%' : '0%'}
          fill="#8884d8"
          dataKey="displayCost"
          label={(entry) => entry.name}
          animationDuration={0}
          isAnimationActive={false}
          stroke={itemData.length > 1 ? DEFAULT_STROKE_COLOR : ''}
        >
          {itemData.map(({ id, color }) => {
            const setActive = () => {
              setActiveKey(id);
            };
            return (
              <Cell
                key={id}
                fill={getChartItemColor(id, activeKey, color)}
                onMouseEnter={setActive}
                onMouseLeave={clearActiveKey}
              />
            );
          })}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
};

export default ItemsChart;
